import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Icon } from '../components/Icon';
import { api, ApiError } from '../lib/api';
import { docStatusTone } from '../lib/docs';
import { money } from '../lib/format';
import { useSession } from '../lib/session';
import type { Deal } from './Deals';
import '../styles/deals.css';

interface Stage {
  code: string;
  label: string;
  index: number;
}

export function DealDetail() {
  const { id } = useParams<{ id: string }>();
  const nav = useNavigate();
  const { company } = useSession();
  const [deal, setDeal] = useState<Deal | null>(null);
  const [stages, setStages] = useState<Stage[]>([]);
  const [err, setErr] = useState<string | null>(null);
  const [actionErr, setActionErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    if (!company) return;
    try {
      const [all, st] = await Promise.all([
        api.get<Deal[]>('/deals'),
        api.get<Stage[]>('/deals/stages'),
      ]);
      const found = all.find((d) => d.id === id);
      if (!found) setErr('Deal not found.');
      else setDeal(found);
      setStages(st);
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Error');
    }
  }, [company, id]);

  useEffect(() => {
    setDeal(null);
    setErr(null);
    void load();
  }, [load]);

  const moveTo = async (stageCode: string) => {
    if (!deal) return;
    setBusy(true);
    setActionErr(null);
    try {
      await api.post(`/deals/${deal.id}/stage`, { stage: stageCode });
      await load();
    } catch (e) {
      setActionErr(e instanceof ApiError ? e.message : 'No se pudo cambiar la etapa.');
    } finally {
      setBusy(false);
    }
  };

  if (err) {
    return (
      <main className="main">
        <button className="doc-back" onClick={() => nav('/operaciones/deals')}>
          <Icon name="arrow-right" size={15} style={{ transform: 'rotate(180deg)' }} /> Deal Rooms
        </button>
        <div className="card"><div className="empty">{err}</div></div>
      </main>
    );
  }
  if (!deal) return <main className="main"><div className="faint" style={{ padding: 40 }}>Loading deal…</div></main>;

  const nextStage = stages[deal.stageIndex + 1];

  return (
    <main className="main">
      <button className="doc-back" onClick={() => nav('/operaciones/deals')}>
        <Icon name="arrow-right" size={15} style={{ transform: 'rotate(180deg)' }} /> Deal Rooms
      </button>

      <div className="doc-head">
        <div>
          <span className="eyebrow">{deal.code}</span>
          <div className="doc-head__num" style={{ marginTop: 6 }}>{deal.title}</div>
          {deal.counterparty && <p className="muted" style={{ marginTop: 8, fontSize: 14 }}>{deal.counterparty.name}</p>}
        </div>
        <div className="doc-head__amt">
          <div className="doc-head__total">{money(deal.valueMinor, deal.currency)}</div>
          <div className="deal-card__stage" style={{ textAlign: 'right' }}>Etapa {deal.stageIndex + 1} de {deal.stageCount}</div>
        </div>
      </div>

      <div className="doc-actions" style={{ borderBottom: 'none', paddingBottom: 8 }}>
        {nextStage ? (
          <button className="btn btn--accent" disabled={busy} onClick={() => moveTo(nextStage.code)}>
            Avanzar a: {nextStage.label}
            <Icon name="arrow-right" size={16} />
          </button>
        ) : (
          <span className="pill pill--active"><span className="pill__dot" />Operation completed</span>
        )}
      </div>
      {actionErr && <div className="act-error"><Icon name="alert" size={16} />{actionErr}</div>}

      {/* Ruta logística */}
      {(deal.origin || deal.destination) && (
        <div className="card" style={{ marginTop: 12, marginBottom: 4 }}>
          <div className="card__body">
            <div className="route-line">
              <div className="route-node">
                <div className="route-node__label">Origen</div>
                <div className="route-node__place">{deal.origin ?? '—'}</div>
              </div>
              <div className="route-track"><span className="route-track__ico"><Icon name="route" size={18} /></span></div>
              <div className="route-node">
                <div className="route-node__label">Destino</div>
                <div className="route-node__place">{deal.destination ?? '—'}</div>
              </div>
              {deal.incoterm && <span className="route-incoterm">{deal.incoterm}</span>}
            </div>
          </div>
        </div>
      )}

      <div className="doc-grid" style={{ marginTop: 20 }}>
        {/* Escalera de etapas */}
        <div className="card">
          <div className="card__head"><span className="card__title">Deal progress</span></div>
          <div className="card__body" style={{ padding: 10 }}>
            <div className="ladder">
              {stages.map((s) => {
                const state = s.index < deal.stageIndex ? 'done' : s.index === deal.stageIndex ? 'current' : 'todo';
                return (
                  <button
                    key={s.code}
                    className={`rung${state === 'done' ? ' rung--done' : ''}${state === 'current' ? ' rung--current' : ''}`}
                    disabled={busy || state === 'current'}
                    onClick={() => moveTo(s.code)}
                    title={state === 'todo' ? 'Saltar a esta etapa' : state === 'done' ? 'Volver a esta etapa' : undefined}
                  >
                    <span className="rung__dot">{state === 'done' ? <Icon name="check" size={14} /> : s.index + 1}</span>
                    <span className="rung__label">{s.label}</span>
                    {state === 'current' && <span className="rung__tag">Actual</span>}
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        {/* Documentos vinculados */}
        <div className="col gap-20">
          <div className="card">
            <div className="card__head"><span className="card__title">Deal documents</span></div>
            <div className="card__body card__body--flush">
              {deal.documents.length === 0 ? (
                <div className="empty" style={{ padding: 18 }}>No linked documents yet.</div>
              ) : (
                deal.documents.map((x) => (
                  <button key={x.id} className="todo-item" onClick={() => nav(`/documentos/${x.id}`)}>
                    <Icon name="file" size={17} className="todo-item__ico" />
                    <span className="todo-item__body">
                      <span className="todo-item__title">{x.number}</span>
                      <span className="todo-item__meta">{x.type}</span>
                    </span>
                    <span className={`pill pill--${docStatusTone(x.status)}`}><span className="pill__dot" />{x.status}</span>
                  </button>
                ))
              )}
            </div>
          </div>

          {deal.sector && (
            <div className="card">
              <div className="card__head"><span className="card__title">Ficha</span></div>
              <div className="card__body" style={{ paddingTop: 6, paddingBottom: 6 }}>
                <div className="meta__row"><span className="meta__k">Sector</span><span className="meta__v">{deal.sector}</span></div>
                <div className="meta__row"><span className="meta__k">Estado</span><span className="meta__v">{deal.isOpen ? 'Abierta' : 'Cerrada'}</span></div>
                <div className="meta__row"><span className="meta__k">Valor</span><span className="meta__v num">{money(deal.valueMinor, deal.currency)}</span></div>
              </div>
            </div>
          )}
        </div>
      </div>
    </main>
  );
}
